import * as React from 'react'
import clx from 'classnames'

type MoneyInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> & {
  value?: string | number | null
  isDirty?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

// Keep only digits and a single decimal separator (comma in UI).
// Normalize dots to commas while typing.
const normalizeTyping = (raw: string): string => {
  // Remove all characters except digits, comma, dot
  let v = raw.replace(/[^\d,\.]/g, '')

  // Normalize dot -> comma
  v = v.replace(/\./g, ',')

  // Allow only one comma
  const firstComma = v.indexOf(',')
  if (firstComma !== -1) {
    v =
      v.slice(0, firstComma + 1) +
      v.slice(firstComma + 1).replace(/,/g, '')
  }

  return v
}

const toUiComma = (value: string | number | null | undefined): string => {
  if (value == null) return '0,00'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '0,00'
    return value.toFixed(2).replace('.', ',')
  }

  // string: normalize to comma for UI
  const v = normalizeTyping(value)
  if (v === '') return '0,00'
  return v
}

// Convert UI comma string to API dot string with 2 decimals: "12,3" -> "12.30"
const toApiDotFixed2 = (ui: string): string | null => {
  const trimmed = ui.trim()
  if (trimmed === '') return null

  const normalized = trimmed.replace(',', '.')
  const n = Number(normalized)
  if (!Number.isFinite(n)) return null

  return n.toFixed(2) // dot separator
}

export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  isDirty,
  onChange,
  onBlur,
  ...rest
}) => {
  const [text, setText] = React.useState<string>(() => toUiComma(value))

  // Sync external value -> UI text (only when it actually changes externally)
  React.useEffect(() => {
    setText(toUiComma(value))
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeTyping(e.target.value)
    setText(next)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // If user left empty, show 0,00
    const ui = text === '' ? '0,00' : text

    // Prepare API value with dot
    const api = toApiDotFixed2(ui)

    // If parse fails, do not change the user's text; just call onBlur
    if (api == null) {
      onBlur?.(e)
      return
    }

    // Update UI text to fixed 2 decimals with comma
    const uiFixed = api.replace('.', ',') // "12.30" -> "12,30"
    setText(uiFixed)

    // Emit change event with dot value (so Number() works upstream)
    e.target.value = api

    const syntheticChange = {
      ...e,
      target: e.target,
      currentTarget: e.target,
    } as unknown as React.ChangeEvent<HTMLInputElement>

    onChange?.(syntheticChange)
    onBlur?.(e)
  }

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      className={clx(rest.className, { 'tw-text-red-400': isDirty })}
      value={text}
      onFocus={()=>{
        if(!value) setText("")
      }}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}
